<script>
  import { API_BASE_URL, EMAIL_CONFIG, isSuperAdmin as checkSuperAdmin, isAdminUser } from './config.js';
  import { onMount, onDestroy } from 'svelte';
  import './styles/main.css';
  import Navigation from './components/layout/Navigation.svelte';
  import LoadingSpinner from './components/common/LoadingSpinner.svelte';
  import ErrorMessage from './components/common/ErrorMessage.svelte';
  import EmptyState from './components/common/EmptyState.svelte';
  import BaseModal from './components/modals/BaseModal.svelte';
  import FormModal from './components/modals/FormModal.svelte';
  import Button from './components/common/Button.svelte';
  import AgGridSvelte from 'ag-grid-svelte';
  import 'ag-grid-community/styles/ag-grid.css';
  import 'ag-grid-community/styles/ag-theme-alpine.css';

  export let setPage;
  export let user;

  let isLoading = false;
  let loadError = null;
  let projects = [];
  let selectedProject = null;
  let applicants = [];
  let isAdmin = false;
  let isSuperAdmin = false;
  let isCompany = false;
  let quickFilterText = '';
  let projectSearchTerm = "";
  let filteredProjects = [];
  let allRawApplicants = [];
  let isDetailModalOpen = false;
  let selectedApplicant = null;
  let isEmailModalOpen = false;
  let emailSubject = '';
  let emailContent = '';
  let isSendingEmail = false;
  let emailSendError = null;
  let emailSendSuccess = false;
  let isProfileFilesModalOpen = false;
  let selectedApplicantFiles = [];
  let isLoadingFiles = false;
  let fileLoadError = null;
  let showExcludedUsers = false;
  let previousApplicationStatus = {};
  let isProjectDetailModalOpen = false;
  let selectedProjectForDetail = null;
  let agGridApi = null;
  let agGridColumnApi = null;
  let filteredApplicants = [];

  const emailConfig = EMAIL_CONFIG;
  const statusOptions = [
    { id: 0, label: '신청됨' },
    { id: 1, label: '검토중' },
    { id: 9, label: '본인확인중' }, // verifying
    { id: 2, label: '진행중' },
    { id: 3, label: '합격' },
    { id: 4, label: '불합격' },
    { id: 5, label: '프로젝트 취소' },
    { id: 6, label: '프로젝트 연기' },
    { id: 7, label: '마감' },
    { id: 8, label: '지원 취소' },
    { id: 10, label: '진행 어려움(스킬)' }, // price_difficulty
    { id: 11, label: '진행 어려움(경험)' } // grade_difficulty
  ];
  const statusMapping = {
    'applied': 0, 'reviewing': 1, 'proceeding': 2, 'accepted': 3, 'rejected': 4,
    'project_canceled': 5, 'project_delayed': 6, 'closed': 7, 'canceled': 8, 'verifying': 9,
    'price_difficulty': 10, 'grade_difficulty': 11
  };




  function calculateAge(birthDateStr) {
    if (!birthDateStr) return '';
    const cleanedDateStr = String(birthDateStr).trim();
    let birthDate;
    if (/^\d{4}$/.test(cleanedDateStr)) {
      birthDate = new Date(parseInt(cleanedDateStr, 10), 0, 1);
    } else {
      birthDate = new Date(cleanedDateStr);
    }
    if (isNaN(birthDate.getTime())) return '';
    let age = new Date().getFullYear() - birthDate.getFullYear();
    const m = new Date().getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && new Date().getDate() < birthDate.getDate())) age--;
    return age > 0 && age < 150 ? age : '';
  }

  function formatPrice(price) {
    if (!price) return '-';
    const numericPrice = String(price).replace(/[^\d]/g, '');
    return numericPrice ? Number(numericPrice).toLocaleString() : '-';
  }

  function isUserExcluded(user) {
    if (!user) return false;
    const val = user.is_excluded ?? user.isExcluded;
    if (typeof val === 'string') return ['Y', 'TRUE', '1'].includes(val.toUpperCase());
    return val === true || val === 1;
  }
  
  function formatCareer(careerYear, careerMonth) {
    const year = Number(careerYear) || 0;
    const month = Number(careerMonth) || 0;
    if (year === 0 && month === 0) return '신입';
    let result = '';
    if (year > 0) result += `${year}년`;
    if (month > 0) result += result ? ` ${month}개월` : `${month}개월`;
    return result || '-';
  }

  onMount(async () => {
    if (typeof window !== 'undefined') {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        try {
          user = JSON.parse(storedUser);
          isAdmin = isAdminUser(user);
          isSuperAdmin = checkSuperAdmin(user.email);
          isCompany = user.role === 'company';
        } catch (e) {
          localStorage.removeItem('user');
        }
      }
    }
    try {
      await fetchProjects();
      await fetchAllApplicants();
    } catch (error) {
      loadError = error.message;
    }
  });

  onDestroy(() => {
    // Cleanup code if needed
  });

  $: {
    if (projectSearchTerm.trim() === "") {
      filteredProjects = projects.filter(p => (p.applicantCount || 0) > 0);
    } else {
      const searchLower = projectSearchTerm.toLowerCase();
      filteredProjects = projects.filter(p =>
        ((p.title?.toLowerCase().includes(searchLower) ||
        p.company?.toLowerCase().includes(searchLower) ||
        p.description?.toLowerCase().includes(searchLower))) && (p.applicantCount || 0) > 0
      );
    }
  }

  $: {
    if (allRawApplicants) {
      const baseData = showExcludedUsers ? allRawApplicants : allRawApplicants.filter(app => !isUserExcluded(app.userInfo) && !isUserExcluded(app.raw));
      
      if (projects.length > 0) {
        const newCounts = {};
        baseData.forEach(app => {
          const pid = String(app.projectId);
          if(pid) {
            newCounts[pid] = (newCounts[pid] || 0) + 1;
          }
        });
        projects = projects.map(p => ({ ...p, applicantCount: newCounts[String(p.id)] || 0 }));
      }

      if (selectedProject) {
        applicants = baseData.filter(app => String(app.projectId) === String(selectedProject.id));
      } else {
        applicants = [];
      }
    }
  }

  $: {
    if (applicants) {
      if (quickFilterText.trim() === '') {
        filteredApplicants = applicants;
      } else {
        const searchLower = quickFilterText.toLowerCase();
        filteredApplicants = applicants.filter(app => 
          app.name?.toLowerCase().includes(searchLower) ||
          app.email?.toLowerCase().includes(searchLower) ||
          app.price?.toString().includes(searchLower)
        );
      }
    }
  }

  function clearSearch() {
    projectSearchTerm = "";
  }

  async function sendEmail(applicant, subject, content) {
    isSendingEmail = true;
    emailSendError = null;
    emailSendSuccess = false;
    try {
      const token = localStorage.getItem('token');
      const receiverEmail = applicant.email && applicant.email !== '-' ? applicant.email : emailConfig.default_receiver;
      const response = await fetch(`${API_BASE_URL}/api/email/send`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ to: receiverEmail, subject, text: content, html: content.replace(/\n/g, '<br>'), from: emailConfig.sender_email, sender_password: emailConfig.password })
      });
      if (!response.ok) throw new Error((await response.json()).message || '이메일 전송에 실패했습니다.');
      emailSendSuccess = true;
      setTimeout(closeEmailModal, 2000);
    } catch (error) {
      emailSendError = error.message;
    } finally {
      isSendingEmail = false;
    }
  }

  function generateEmailTemplate(applicant) {
    const applicantName = applicant.name || '지원자님';
    const projectTitle = applicant.projectTitle || '프로젝트';
    
    switch (applicant.status) {
      case 3: emailSubject = `[WIDE-WORK] ${projectTitle} 합격 안내`; break;
      case 4: emailSubject = `[WIDE-WORK] ${projectTitle} 불합격 안내`; break;
      default: emailSubject = `[WIDE-WORK] ${projectTitle} 지원 결과 안내`;
    }

    emailContent = `안녕하세요, ${applicantName}님...`; // Simplified content
    isEmailModalOpen = true;
  }

  function closeEmailModal() {
    isEmailModalOpen = false;
  }

  async function fetchProjects() {
    isLoading = true;
    loadError = null;
    try {
      const token = localStorage.getItem('token');
      let apiUrl = `${API_BASE_URL}/api/projects`;
      if (isCompany && !isAdmin && user && user.id) {
        apiUrl = `${API_BASE_URL}/api/company/projects?userId=${user.id}`;
      }
      const response = await fetch(apiUrl, { headers: { 'Authorization': `Bearer ${token}` } });
      if (!response.ok) throw new Error('프로젝트 목록을 불러오는 데 실패했습니다.');
      let data = await response.json();
      let allProjects = Array.isArray(data) ? data : (data.projects || data.data || []);
      
      const countsResponse = await fetch(`${API_BASE_URL}/api/projects/applicants/counts`, { headers: { 'Authorization': `Bearer ${token}` } });
      if(countsResponse.ok) {
        const countsData = await countsResponse.json();
        if (countsData && countsData.counts) {
            allProjects = allProjects.map(p => ({ ...p, applicantCount: countsData.counts[String(p.id)] || 0 }));
        }
      }
      projects = allProjects;
      filteredProjects = allProjects;
    } catch (error) {
      loadError = error.message;
    } finally {
      isLoading = false;
    }
  }

  async function fetchAllApplicants() {
    isLoading = true;
    loadError = null;
    try {
      const token = localStorage.getItem('token');
      let apiUrl = `${API_BASE_URL}/api/applications/all`;
      if (isSuperAdmin) apiUrl = `${API_BASE_URL}/api/applications/admin`;
      
      const response = await fetch(apiUrl, { headers: { 'Authorization': `Bearer ${token}` } });
      if (!response.ok) throw new Error('지원자 정보를 가져오는데 실패했습니다.');
      
      const data = await response.json();
      const applications = data.applications || (Array.isArray(data) ? data : []);
      
      if (applications.length > 0) {
        const users = await fetchUsersData();
        processApplicationsWithUsers(applications, users);
      }
    } catch (error) {
      loadError = error.message;
    } finally {
      isLoading = false;
    }
  }

  async function fetchUsersData() {
    const token = localStorage.getItem('token');
    const endpoints = [`${API_BASE_URL}/api/users`, `${API_BASE_URL}/api/users/all`];
    for (const endpoint of endpoints) {
      try {
        const response = await fetch(endpoint, { headers: { 'Authorization': `Bearer ${token}` } });
        if (response.ok) {
          const data = await response.json();
          return data.users || (Array.isArray(data) ? data : []);
        }
      } catch (e) { /* ignore */ }
    }
    return [];
  }

  function processApplicationsWithUsers(applications, users) {
    const userMap = new Map(users.map(u => [String(u.id || u._id), u]));
    const projectIds = [...new Set(applications.map(app => app.projectId || app.project_id))].filter(Boolean);

    fetchProjectsData(projectIds).then(projectsData => {
      const projectMap = new Map(projectsData.map(p => [String(p.id || p._id), p]));
      
      allRawApplicants = applications.map(app => {
        const userId = String(app.userId || app.user_id);
        const projectId = String(app.projectId || app.project_id);
        const userInfo = userMap.get(userId);
        const projectInfo = projectMap.get(projectId);
        
        let status = app.status;
        if (typeof status === 'string') status = statusMapping[status] ?? 0;
        else if (status == null) status = 0;

        return {
          id: String(app.id || app._id),
          userId,
          name: userInfo?.name || app.name || '이름 없음',
          projectId,
          projectTitle: projectInfo?.title || '프로젝트명 없음',
          email: userInfo?.email || app.email || '-',
          status,
          applied_at: app.applied_at || app.createdAt,
          age: userInfo?.age || calculateAge(userInfo?.birth_date),
          price: formatPrice(app.price),
          isBlacklist: userInfo?.is_blacklist === 'Y' || app.is_blacklist === 'Y',
          raw: app,
          userInfo
        };
      });
    });
  }

  async function fetchProjectsData(projectIds) {
    if (projectIds.length === 0) return [];
    const token = localStorage.getItem('token');
    try {
        const response = await fetch(`${API_BASE_URL}/api/projects`, { headers: { 'Authorization': `Bearer ${token}` } });
        if(!response.ok) return [];
        const data = await response.json();
        const allProjects = data.projects || (Array.isArray(data) ? data : []);
        return allProjects.filter(p => projectIds.includes(String(p.id)));
    } catch(e) {
        return [];
    }
  }

  function selectProject(project) {
    selectedProject = project;
    const projectId = String(project.id);
    const baseData = showExcludedUsers ? allRawApplicants : allRawApplicants.filter(app => !isUserExcluded(app.userInfo) && !isUserExcluded(app.raw));
    applicants = baseData.filter(app => String(app.projectId) === projectId);
    if(applicants.length === 0) {
        fetchProjectApplicants(project);
    }
  }

  async function fetchProjectApplicants(project) {
    isLoading = true;
    try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_BASE_URL}/api/projects/${project.id}/applications`, { headers: { 'Authorization': `Bearer ${token}` } });
        if(!response.ok) throw new Error("지원자 목록 로드 실패");
        const data = await response.json();
        const projectApplicants = data.applications || (Array.isArray(data) ? data : []);
        const users = await fetchUsersData();
        const userMap = new Map(users.map(u => [String(u.id || u._id), u]));

        applicants = projectApplicants.map(app => {
            const userId = String(app.userId || app.user_id);
            const userInfo = userMap.get(userId);
            return {
                id: String(app.id || app._id),
                userId,
                name: userInfo?.name || app.name || '이름 없음',
                projectId: project.id,
                projectTitle: project.title,
                email: userInfo?.email || app.email || '-',
                status: typeof app.status === 'string' ? (statusMapping[app.status] ?? 0) : (app.status ?? 0),
                applied_at: app.applied_at || app.createdAt,
                age: userInfo?.age || calculateAge(userInfo?.birth_date),
                price: formatPrice(app.price),
                isBlacklist: userInfo?.is_blacklist === 'Y' || app.is_blacklist === 'Y',
                raw: app,
                userInfo
            };
        }).filter(app => showExcludedUsers || !isUserExcluded(app.userInfo));
    } catch (e) {
        loadError = e.message;
    } finally {
        isLoading = false;
    }
  }

  async function updateApplicantStatus(applicant, newStatus) {
    const token = localStorage.getItem('token');
    const applicationId = applicant.id;
    const previousStatus = applicant.status;
    if (String(previousStatus) === String(newStatus)) return;

    previousApplicationStatus[applicationId] = previousStatus;
    try {
      const response = await fetch(`${API_BASE_URL}/api/applications/${applicationId}/status`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      if (!response.ok) throw new Error((await response.json()).message || '상태 업데이트 실패');
      
      applicant.status = newStatus;

      if ([3, 4, 5, 6].includes(Number(newStatus))) {
        generateEmailTemplate({ ...applicant, previousStatus });
      }
    } catch (error) {
      alert(`오류: ${error.message}`);
      applicant.status = previousStatus;
    }
  }

  function viewApplicantDetail(applicant) {
    selectedApplicant = applicant;
    isDetailModalOpen = true;
  }

  async function fetchApplicantFiles(applicant) {
    isLoadingFiles = true;
    fileLoadError = null;
    isProfileFilesModalOpen = true;
    selectedApplicantFiles = [];
    try {
      const token = localStorage.getItem('token');
      const userId = applicant.userId;
      if (!userId) throw new Error("사용자 ID가 없습니다.");
      
      const response = await fetch(`${API_BASE_URL}/api/users/${userId}/files`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (!response.ok) throw new Error("첨부 문서를 가져오는 데 실패했습니다.");
      
      const data = await response.json();
      const files = data.files || (Array.isArray(data) ? data : []);
      selectedApplicantFiles = files.map(file => ({ ...file, downloadUrl: `${API_BASE_URL}/api/files/download/${file.id}` }));
    } catch (error) {
      fileLoadError = error.message;
    } finally {
      isLoadingFiles = false;
    }
  }

  async function openProjectDetailModal(projectId) {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (!response.ok) throw new Error('프로젝트 정보를 가져오는 데 실패했습니다.');
      selectedProjectForDetail = await response.json();
      isProjectDetailModalOpen = true;
    } catch (error) {
      alert(error.message);
    }
  }

  function countProjectApplicants(projectId) {
    if (!allRawApplicants) return 0;
    const projectIdStr = String(projectId);
    const baseData = showExcludedUsers ? allRawApplicants : allRawApplicants.filter(app => !isUserExcluded(app.userInfo) && !isUserExcluded(app.raw));
    return baseData.filter(app => String(app.projectId) === projectIdStr).length;
  }

  function getColumnDefs() {
    return [
      {
        headerName: '이름',
        field: 'name',
        width: 120,
        cellRenderer: params => {
          const value = params.value || '이름 없음';
          const isBlacklist = params.data.isBlacklist;
          return isBlacklist ? 
            `<div class="applicant-name"><span class="applicant-name-text">${value}</span><span class="blacklist-tag">제외</span></div>` : 
            `<div class="applicant-name"><span class="applicant-name-text">${value}</span></div>`;
        }
      },
      {
        headerName: '나이',
        field: 'age',
        width: 80,
        cellRenderer: params => params.value || '-'
      },
      {
        headerName: '단가(만원)',
        field: 'price',
        width: 120,
        cellRenderer: params => params.value || '-'
      },
      {
        headerName: '지원일자',
        field: 'applied_at',
        width: 130,
        cellRenderer: params => {
          if (!params.value) return '-';
          const date = new Date(params.value);
          return date.toLocaleDateString('ko-KR', {
            year: '2-digit',
            month: '2-digit',
            day: '2-digit'
          }).replace(/\. /g, '.').replace(/\.$/, '');
        }
      },
      {
        headerName: '상태',
        field: 'status',
        width: 130,
        cellRenderer: params => {
          const status = params.value ?? 0;
          return `<select class="status-select" data-app-id="${params.data.id}" data-current-status="${status}">
            ${statusOptions.map(opt => `<option value="${opt.id}" ${opt.id === status ? 'selected' : ''}>${opt.label}</option>`).join('')}
          </select>`;
        }
      },
      {
        headerName: '첨부문서',
        field: 'attachments',
        width: 100,
        cellRenderer: params => {
          return `<button class="profile-docs-btn small" data-user-id="${params.data.userId}" data-app-id="${params.data.id}">문서</button>`;
        }
      },
      {
        headerName: '관리',
        field: 'actions',
        width: 100,
        cellRenderer: params => {
          return `<button class="detail-btn small" data-app-id="${params.data.id}">상세</button>`;
        }
      }
    ];
  }

  function onGridReady(params) {
    agGridApi = params.api;
    agGridColumnApi = params.columnApi;
    
    // 그리드 이벤트 리스너 등록
    params.api.addEventListener('cellClicked', handleCellClick);
  }

  function handleCellClick(event) {
    const target = event.event?.target;
    if (!target) return;

    // 상태 변경 드롭다운 처리
    if (target.classList.contains('status-select')) {
      const appId = target.getAttribute('data-app-id');
      const currentStatus = parseInt(target.getAttribute('data-current-status'));
      const newStatus = parseInt(target.value);
      
      if (newStatus !== currentStatus) {
        const applicant = filteredApplicants.find(app => app.id === appId);
        if (applicant) {
          updateApplicantStatus(applicant, newStatus);
        }
      }
    }

    // 첨부문서 버튼 처리
    if (target.classList.contains('profile-docs-btn')) {
      const appId = target.getAttribute('data-app-id');
      const applicant = filteredApplicants.find(app => app.id === appId);
      if (applicant) {
        fetchApplicantFiles(applicant);
      }
    }

    // 상세보기 버튼 처리
    if (target.classList.contains('detail-btn')) {
      const appId = target.getAttribute('data-app-id');
      const applicant = filteredApplicants.find(app => app.id === appId);
      if (applicant) {
        viewApplicantDetail(applicant);
      }
    }
  }

</script>

<svelte:head>
  <title>프로젝트별 지원자 관리 - WIDE-WORK</title>
</svelte:head>

<div class="admin-dashboard">
  <Navigation {setPage} {user} />

  <main class="main-content">
    <div class="container">
      <div class="page-header">
        <h1>프로젝트별 지원자 관리</h1>
        <div class="header-actions">
          <label class="checkbox-label">
            <input type="checkbox" bind:checked={showExcludedUsers}>
            제외된 회원 보기
          </label>
        </div>
      </div>

      {#if loadError}
        <ErrorMessage message={loadError} />
      {/if}

      <div class="content-wrapper">
        <div class="project-list-container">
          <div class="project-list-header">
            공고 목록 ({filteredProjects.length}개)
          </div>
          <div class="search-box">
            <div class="search-input-wrapper">
              <input type="text" placeholder="프로젝트 검색..." bind:value={projectSearchTerm} />
              <button class="clear-search-btn" on:click={clearSearch}>×</button>
            </div>
          </div>
          <div class="project-list">
            {#if isLoading && projects.length === 0}
              <LoadingSpinner />
            {:else if filteredProjects.length > 0}
              <ul>
                {#each filteredProjects as project}
                  <li>
                    <button on:click={() => selectProject(project)} class="project-item" class:selected={selectedProject && selectedProject.id === project.id}>
                      <div class="project-info">
                        <div class="project-main-info">
                          <span class="project-title">{project.title}</span>
                          <div class="company-row">
                            <span class="company-name">{project.company || '-'}</span>
                            <span class="applicant-count">지원 {project.applicantCount || 0}건</span>
                          </div>
                        </div>
                      </div>
                    </button>
                  </li>
                {/each}
              </ul>
            {:else}
              <EmptyState message="표시할 프로젝트가 없습니다." />
            {/if}
          </div>
        </div>

        <div class="applicant-list-container">
          {#if selectedProject}
            <div class="applicant-header">
              <h2>{selectedProject.title} - 지원자 목록</h2>
              <div class="quick-filter">
                <input type="text" placeholder="이름, 이메일 등 검색..." bind:value={quickFilterText} />
              </div>
            </div>
            <div class="applicant-table-container">
              {#if filteredApplicants.length > 0}
                <div class="ag-theme-alpine" style="height: 100%; width: 100%;">
                  <AgGridSvelte
                    bind:api={agGridApi}
                    bind:columnApi={agGridColumnApi}
                    rowData={filteredApplicants}
                    columnDefs={getColumnDefs()}
                    defaultColDef={{
                      resizable: true,
                      sortable: true,
                      filter: false,
                      cellStyle: { fontSize: '14px' }
                    }}
                    domLayout="normal"
                    headerHeight={44}
                    rowHeight={50}
                    suppressRowClickSelection={true}
                    rowSelection="multiple"
                    enableCellTextSelection={true}
                    overlayNoRowsTemplate="<span>지원자가 없습니다.</span>"
                    localeText={{
                      noRowsToShow: '지원자가 없습니다.',
                      loading: '로딩 중...',
                      page: '페이지',
                      more: '더보기',
                      to: '~',
                      of: '/',
                      next: '다음',
                      last: '마지막',
                      first: '첫번째',
                      previous: '이전',
                    }}
                    on:gridReady={onGridReady}
                  />
                </div>
              {:else}
                <EmptyState message="지원자가 없습니다." />
              {/if}
            </div>
          {:else}
            <EmptyState message="프로젝트를 선택하여 지원자를 확인하세요." />
          {/if}
        </div>
      </div>
    </div>
  </main>
</div>

<!-- Modals -->
{#if isDetailModalOpen}
  <BaseModal on:close={() => isDetailModalOpen = false}>
    <div class="applicant-detail-modal">
      {#if selectedApplicant}
        <h2>{selectedApplicant.name}님 상세 정보</h2>
        <div class="detail-section">
          <h3>기본 정보</h3>
          <p><strong>이름:</strong> {selectedApplicant.name}</p>
          <p><strong>이메일:</strong> {selectedApplicant.email || '-'}</p>
          <p><strong>나이:</strong> {selectedApplicant.age || '-'}</p>
          <p><strong>희망 단가:</strong> {selectedApplicant.price || '-'} 만원</p>
          <p><strong>경력:</strong> {formatCareer(selectedApplicant.userInfo?.career_year, selectedApplicant.userInfo?.career_month)}</p>
          <p><strong>성별:</strong> {selectedApplicant.userInfo?.gender || '-'}</p>
        </div>
        <div class="modal-actions">
          <Button on:click={() => isDetailModalOpen = false}>닫기</Button>
        </div>
      {/if}
    </div>
  </BaseModal>
{/if}

{#if isEmailModalOpen}
  <FormModal title="결과 안내 이메일 발송" on:close={closeEmailModal}>
    <form on:submit|preventDefault={() => sendEmail(selectedApplicant, emailSubject, emailContent)}>
      <div class="form-group">
        <label for="email-subject">제목</label>
        <input id="email-subject" type="text" bind:value={emailSubject} required>
      </div>
      <div class="form-group">
        <label for="email-content">내용</label>
        <textarea id="email-content" rows="10" bind:value={emailContent} required></textarea>
      </div>
      {#if isSendingEmail} <LoadingSpinner /> {:else if emailSendSuccess} <p class="success-message">이메일이 성공적으로 전송되었습니다.</p> {:else if emailSendError} <ErrorMessage message={emailSendError} /> {/if}
      <div class="form-actions">
        <Button type="submit" disabled={isSendingEmail}>{isSendingEmail ? '전송 중...' : '전송'}</Button>
        <Button type="button" on:click={closeEmailModal} style="secondary">취소</Button>
      </div>
    </form>
  </FormModal>
{/if}

{#if isProfileFilesModalOpen}
  <BaseModal on:close={() => isProfileFilesModalOpen = false}>
    <div class="profile-files-modal">
      <h3>첨부 문서 목록</h3>
      {#if isLoadingFiles} <LoadingSpinner /> {:else if fileLoadError} <ErrorMessage message={fileLoadError} /> {:else if selectedApplicantFiles.length > 0}
        <ul>
          {#each selectedApplicantFiles as file}
            <li><a href={file.downloadUrl} target="_blank" rel="noopener noreferrer">{file.original_name || file.fileName}</a> <span>({(file.size / 1024).toFixed(2)} KB)</span></li>
          {/each}
        </ul>
      {:else} <EmptyState message="첨부된 문서가 없습니다." /> {/if}
      <div class="modal-actions">
        <Button on:click={() => isProfileFilesModalOpen = false}>닫기</Button>
      </div>
    </div>
  </BaseModal>
{/if}

{#if isProjectDetailModalOpen}
  <BaseModal on:close={() => isProjectDetailModalOpen = false}>
    <div class="project-detail-modal">
      {#if selectedProjectForDetail}
        <h2>{selectedProjectForDetail.title}</h2>
        <p><strong>회사:</strong> {selectedProjectForDetail.company}</p>
        <p><strong>설명:</strong> {selectedProjectForDetail.description}</p>
        <div class="modal-actions">
          <Button on:click={() => isProjectDetailModalOpen = false}>닫기</Button>
        </div>
      {:else} <LoadingSpinner /> {/if}
    </div>
  </BaseModal>
{/if}

<style>
  .admin-dashboard { display: flex; flex-direction: column; height: 100vh; background-color: #f8f9fa; }
  .main-content { flex-grow: 1; padding: 0.5rem 0; overflow-y: auto; background-color: #f8f9fa; min-height: calc(100vh - 80px); max-width: 100%; margin: 0 auto; width: 100%; }
  .container { max-width: 100%; margin: 0 auto; padding: 0 1rem; width: 100%; }
  .page-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 1.5rem; padding: 1.5rem; background-color: #fff; border-radius: 12px; box-shadow: 0 2px 8px rgba(0,0,0,0.06); }
  .page-header h1 { font-size: 1.5rem; font-weight: 700; color: #2c3e50; margin: 0; }
  .header-actions .checkbox-label { display: flex; align-items: center; font-size: 0.9rem; color: #6c757d; }
  .header-actions input[type="checkbox"] { margin-right: 0.5rem; }
  .content-wrapper { display: grid; grid-template-columns: 320px 1fr; gap: 1.5rem; height: calc(100vh - 180px); }
  
  /* 프로젝트 목록 컨테이너 */
  .project-list-container { background-color: #fff; border-radius: 12px; box-shadow: 0 2px 8px rgba(0,0,0,0.06); padding: 0; display: flex; flex-direction: column; height: 100%; overflow: hidden; }
  .search-box { padding: 1.5rem 1.5rem 0 1.5rem; margin-bottom: 1rem; }
  .search-input-wrapper { position: relative; }
  .search-box input { width: 100%; padding: 12px 40px 12px 16px; border: 1px solid #e9ecef; border-radius: 8px; font-size: 14px; background-color: #f8f9fa; transition: all 0.2s ease; }
  .search-box input:focus { outline: none; border-color: #4A90E2; background-color: #fff; box-shadow: 0 0 0 3px rgba(74, 144, 226, 0.1); }
  .clear-search-btn { 
    position: absolute; 
    right: 12px; 
    top: 50%; 
    transform: translateY(-50%); 
    background: transparent; 
    border: none; 
    font-size: 18px; 
    cursor: pointer; 
    color: #6c757d;
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: color 0.2s ease;
  }
  .clear-search-btn:hover {
    color: #343a40;
  }
  .project-list-header { padding: 1.5rem 1.5rem 0.5rem 1.5rem; font-size: 16px; font-weight: 700; color: #2c3e50; border-bottom: none; }
  .project-list { flex-grow: 1; overflow-y: auto; padding: 0 1.5rem 1.5rem 1.5rem; }
  .project-list ul { list-style: none; padding: 0; margin: 0; }
  .project-list li { margin-bottom: 8px; border-radius: 8px; transition: all 0.2s ease; border: 1px solid transparent; }
  .project-list button { width: 100%; background: none; border: none; padding: 12px 16px; margin: 0; font: inherit; cursor: pointer; text-align: left; border-radius: 8px; transition: all 0.2s ease; }
  .project-list li:hover { background-color: #f8f9fa; border-color: #e9ecef; }
  .project-list li .selected { background-color: #e3f2fd; border-color: #4A90E2; color: #1976d2; }
  .project-info { display: flex; width: 100%; }
  .project-main-info { flex-grow: 1; display: flex; flex-direction: column; gap: 4px; }
  .project-title { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; font-weight: 500; font-size: 14px; color: #2c3e50; line-height: 1.2; }
  .company-row { display: flex; justify-content: space-between; align-items: center; }
  .company-name { font-size: 12px; color: #6c757d; font-weight: 400; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; line-height: 1.2; flex-grow: 1; }
  .applicant-count { margin-left: 0.5rem; font-size: 12px; color: #4A90E2; font-weight: 600; background-color: #e3f2fd; padding: 2px 8px; border-radius: 12px; flex-shrink: 0; }
  
  /* 지원자 목록 컨테이너 */
  .applicant-list-container { background-color: #fff; border-radius: 12px; box-shadow: 0 2px 8px rgba(0,0,0,0.06); padding: 1.5rem; display: flex; flex-direction: column; height: 100%; }
  .applicant-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 1.5rem; padding-bottom: 1rem; border-bottom: 1px solid #e9ecef; }
  .applicant-header h2 { font-size: 1.3rem; font-weight: 700; margin: 0; color: #2c3e50; }
  .quick-filter input { padding: 10px 16px; border: 1px solid #e9ecef; border-radius: 8px; width: 250px; font-size: 14px; background-color: #f8f9fa; transition: all 0.2s ease; }
  .quick-filter input:focus { outline: none; border-color: #4A90E2; background-color: #fff; box-shadow: 0 0 0 3px rgba(74, 144, 226, 0.1); }
  .applicant-table-container { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; }

  
  /* 상태 태그 스타일링 */
  :global(.applicant-blacklist-tag), :global(.blacklist-tag) {
    background-color: #dc3545;
    color: white;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 500;
  }
  :global(.blacklist-tag) {
    margin-left: 8px;
  }

  /* 버튼 스타일 */
  :global(.profile-docs-btn), :global(.detail-btn) { 
    padding: 6px 12px; 
    border: none; 
    border-radius: 6px; 
    color: white; 
    cursor: pointer; 
    font-size: 12px;
    font-weight: 500;
    transition: all 0.2s ease;
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }
  :global(.profile-docs-btn) { 
    background-color: #007bff; 
  }
  :global(.profile-docs-btn:hover) {
    background-color: #0056b3;
  }
  :global(.profile-docs-btn.small), :global(.detail-btn.small) {
    padding: 4px 8px;
    font-size: 11px;
  }
  :global(.profile-docs-btn:hover), :global(.detail-btn:hover) {
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  }
  :global(.detail-btn) { 
    background-color: #28a745; 
  }
  :global(.detail-btn:hover) {
    background-color: #218838;
  }

  /* 상태 선택 드롭다운 스타일링 */
  :global(.status-select) {
    width: 100%; 
    padding: 6px 8px; 
    border-radius: 6px; 
    border: 1px solid #e9ecef; 
    font-size: 13px;
    background-color: #fff;
    transition: all 0.2s ease;
    min-width: 100px;
  }
  :global(.status-select:focus) {
    outline: none;
    border-color: #4A90E2;
    box-shadow: 0 0 0 2px rgba(74, 144, 226, 0.1);
  }

  /* 이름 셀 스타일링 */
  :global(.applicant-name) {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  :global(.applicant-name-text) {
    font-weight: 500;
    color: #2c3e50;
  }
  
  /* 모달 스타일 */
  .applicant-detail-modal, .profile-files-modal, .project-detail-modal { 
    padding: 2rem; 
    max-width: 600px; 
    width: 90vw; 
    border-radius: 16px;
  }
  .applicant-detail-modal h2, .profile-files-modal h3, .project-detail-modal h2 { 
    margin-top: 0; 
    font-size: 1.5rem; 
    font-weight: 700;
    padding-bottom: 1rem; 
    border-bottom: 1px solid #e9ecef; 
    color: #2c3e50;
  }
  .detail-section { margin-bottom: 1.5rem; }
  .detail-section h3 { 
    font-size: 1.1rem; 
    color: #4A90E2; 
    margin-bottom: 1rem;
    font-weight: 600;
  }
  .detail-section p { 
    margin: 0.8rem 0; 
    display: flex;
    align-items: center;
    padding: 8px 0;
  }
  .detail-section p strong { 
    margin-right: 1rem; 
    min-width: 80px;
    color: #6c757d;
    font-weight: 500;
  }
  .modal-actions { 
    display: flex; 
    justify-content: flex-end; 
    margin-top: 2rem; 
    padding-top: 1rem; 
    border-top: 1px solid #e9ecef; 
  }
  
  .form-group { margin-bottom: 1.5rem; }
  .form-group label { 
    display: block; 
    margin-bottom: 0.5rem; 
    font-weight: 600; 
    color: #2c3e50;
  }
  .form-group input, .form-group textarea { 
    width: 100%; 
    padding: 12px; 
    border: 1px solid #e9ecef; 
    border-radius: 8px; 
    font-size: 14px;
    transition: all 0.2s ease;
  }
  .form-group input:focus, .form-group textarea:focus {
    outline: none;
    border-color: #4A90E2;
    box-shadow: 0 0 0 3px rgba(74, 144, 226, 0.1);
  }
  .form-actions { 
    display: flex; 
    justify-content: flex-end; 
    gap: 1rem; 
    margin-top: 2rem; 
  }
  .success-message { 
    color: #28a745; 
    text-align: center; 
    margin: 1rem 0; 
    font-weight: 500;
  }
  
  .profile-files-modal ul { list-style: none; padding: 0; }
  .profile-files-modal li { 
    padding: 1rem 0; 
    border-bottom: 1px solid #e9ecef; 
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .profile-files-modal a { 
    color: #4A90E2; 
    text-decoration: none; 
    font-weight: 500;
  }
  .profile-files-modal a:hover { text-decoration: underline; }
  .profile-files-modal span { 
    margin-left: 1rem; 
    font-size: 0.85rem; 
    color: #6c757d;
  }
  
  @media (max-width: 768px) {
    .main-content { padding: 1rem; }
    .content-wrapper { 
      grid-template-columns: 1fr; 
      height: auto; 
      gap: 1rem;
    }
    .project-list-container { 
      height: 300px; 
      margin-bottom: 1rem; 
    }
    .applicant-list-container { height: auto; }
    .page-header { 
      padding: 1rem; 
      margin-bottom: 1rem;
    }
    .page-header h1 { font-size: 1.3rem; }
  }
</style>